import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, withCtx as _withCtx, createBlock as _createBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "organization-form" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = {
  key: 0,
  class: "mb-3"
}
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { key: 2 }

import { ref, watch, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import {
  OrganizationCreateDocument,
  OrganizationUpdateDocument,
  OrganizationType,
} from '@/gql/graphql'; // from codegen
import {
  BAlert,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BSpinner,
} from 'bootstrap-vue-next';
import { CountrySelectOptions } from '@/utils/cloud-utils';

interface Props {
  organization?: Partial<OrganizationType>;
  headerText?: string;
  editable?: boolean;
  buttonText?: string;
  showAdminFields?: boolean;
}
export interface IOrganizationFormValues extends OrganizationType {
  ownerEmail: string | null;
  clearSalesforceId: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'OrganizationForm',
  props: {
    organization: {},
    headerText: {},
    editable: { type: Boolean },
    buttonText: {},
    showAdminFields: { type: Boolean }
  },
  emits: ['organizationCreated', 'organizationUpdated'],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emits = __emit;

// Determine the country selection validation state
const showValidation = ref<boolean>(false);
const countryValidationState = computed(() => {
  if (showValidation.value) {
    return formValues.value.addressCountryCode !== null;
  }
  return null;
});

// local form values (extended organization type)
const formValues = ref<Partial<IOrganizationFormValues>>({});

// Watch the incoming org => update local form
watch(
  () => props.organization,
  (newVal) => {
    formValues.value = {
      name: newVal?.name || '',
      salesforceId: newVal?.salesforceId,
      addressStreet: newVal?.addressStreet || '',
      addressCity: newVal?.addressCity || '',
      addressState: newVal?.addressState || '',
      addressPostalCode: newVal?.addressPostalCode || '',
      addressCountryCode: newVal?.addressCountryCode || '',
    };
  },
  { immediate: true }
);

// --- Create/Update Organization Mutations ---
const mutationInProgress = ref(false);
const mutationShowSuccess = ref(false);
const mutationError = ref<string | null>(null);

const {
  mutate: createOrganizationMut,
  onDone: onCreateOrganizationDone,
  onError: onCreateOrganizationError,
} = useMutation(OrganizationCreateDocument);

const {
  mutate: updateOrganizationMut,
  onDone: onUpdateOrganizationDone,
  onError: onUpdateOrganizationError,
} = useMutation(OrganizationUpdateDocument);

onCreateOrganizationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.organizationCreate) {
    console.debug('Organization created:', res.data.organizationCreate);
    mutationShowSuccess.value = true;
    emits('organizationCreated');
    setTimeout(() => {
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    console.error('Organization creation failed:', res.data);
    mutationError.value = 'Organization creation failed: ' + res.data;
  }
});

onCreateOrganizationError((err) => {
  console.error('Error creating organization:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

onUpdateOrganizationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.organizationUpdate) {
    console.debug('Organization updated:', res.data.organizationUpdate);
    mutationShowSuccess.value = true;
    emits('organizationUpdated');
    setTimeout(() => {
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    console.error('Organization update failed:', res.data);
    mutationError.value = 'Organization update failed: ' + res.data;
  }
});

onUpdateOrganizationError((err) => {
  console.error('Error updating organization:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers
function onSubmit() {
  showValidation.value = true;
  if (countryValidationState.value) {
    showValidation.value = false;

    // Check orgCreateInput.ownerEmail is not empty string, otherwise use null
    if (!formValues.value.ownerEmail) {
      formValues.value.ownerEmail = null;
    }

    // Check Salesforce ID is not empty string, otherwise use null
    if (!formValues.value.salesforceId) {
      formValues.value.salesforceId = null;
      formValues.value.clearSalesforceId = true;
    } else {
      formValues.value.clearSalesforceId = false;
      try {
        formValues.value.salesforceId = Number(formValues.value.salesforceId);
      } catch (e) {
        mutationError.value = 'Salesforce ID must be a number';
        console.error('Salesforce ID must be a number');
        return;
      }
    }

    // Create or update organization
    mutationInProgress.value = true;
    mutationError.value = null;

    if (!props.organization) {
      console.log('Creating organization:', formValues.value);
      createOrganizationMut({
        ownerEmail: formValues.value.ownerEmail,
        name: formValues.value.name,
        salesforceId: formValues.value.salesforceId,
        addressStreet: formValues.value.addressStreet,
        addressCity: formValues.value.addressCity,
        addressState: formValues.value.addressState,
        addressPostalCode: formValues.value.addressPostalCode,
        addressCountryCode: formValues.value.addressCountryCode,
      });
    } else {
      console.log('Updating organization:', formValues.value);
      updateOrganizationMut({
        id: props.organization.id,
        name: formValues.value.name,
        salesforceId: formValues.value.salesforceId,
        clearSalesforceId: formValues.value.clearSalesforceId,
        addressCity: formValues.value.addressCity,
        addressCountryCode: formValues.value.addressCountryCode,
        addressPostalCode: formValues.value.addressPostalCode,
        addressState: formValues.value.addressState,
        addressStreet: formValues.value.addressStreet,
      });
    }
  }
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (props.headerText)
      ? (_openBlock(), _createElementBlock("h4", _hoisted_2, [
          _createVNode(_component_font_awesome_icon, {
            icon: ['fas', 'building'],
            class: "me-2"
          }),
          _createTextVNode(" " + _toDisplayString(props.headerText), 1)
        ]))
      : _createCommentVNode("", true),
    _createVNode(_unref(BForm), {
      onSubmit: _withModifiers(onSubmit, ["prevent"])
    }, {
      default: _withCtx(() => [
        (props.showAdminFields && !props.organization)
          ? (_openBlock(), _createBlock(_unref(BFormGroup), {
              key: 0,
              label: "Organization`s Owner E-mail",
              "label-class": "pb-0 mb-0 mt-1"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  disabled: !props.editable,
                  modelValue: formValues.value.ownerEmail,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formValues.value.ownerEmail) = $event)),
                  name: "organization-owner-email"
                }, null, 8, ["disabled", "modelValue"]),
                (props.editable)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[9] || (_cache[9] = [
                      _createElementVNode("small", {
                        class: "text-secondary",
                        variant: "secondary"
                      }, [
                        _createElementVNode("ol", null, [
                          _createElementVNode("li", null, [
                            _createTextVNode(" Fill in if you want to "),
                            _createElementVNode("strong", null, "delegate organization ownership"),
                            _createTextVNode(", otherwise "),
                            _createElementVNode("strong", null, "you will be the owner"),
                            _createTextVNode(". ")
                          ]),
                          _createElementVNode("li", null, [
                            _createTextVNode(" The user "),
                            _createElementVNode("strong", null, "does not need to be registered"),
                            _createTextVNode(" on the platform, email invite will be sent. ")
                          ]),
                          _createElementVNode("li", null, [
                            _createTextVNode(" The suggested organization owner "),
                            _createElementVNode("strong", null, "can be part of other organization"),
                            _createTextVNode(". Users can switch between organizations they are part of. ")
                          ])
                        ])
                      ], -1)
                    ]))]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createVNode(_unref(BFormGroup), {
          label: "Organization Name",
          "label-class": "pb-0 mb-0 mt-1"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BFormInput), {
              disabled: !props.editable,
              modelValue: formValues.value.name,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formValues.value.name) = $event)),
              name: "organization-name",
              required: ""
            }, null, 8, ["disabled", "modelValue"])
          ]),
          _: 1
        }),
        (props.showAdminFields)
          ? (_openBlock(), _createBlock(_unref(BFormGroup), {
              key: 1,
              label: "Salesforce ID",
              "label-class": "pb-0 mb-0 mt-1"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  disabled: !props.editable,
                  modelValue: formValues.value.salesforceId,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formValues.value.salesforceId) = $event)),
                  name: "salesforce-id",
                  type: "number"
                }, null, 8, ["disabled", "modelValue"])
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createVNode(_unref(BFormGroup), {
          label: "Street",
          "label-class": "pb-0 mb-0 mt-1"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BFormInput), {
              disabled: !props.editable,
              modelValue: formValues.value.addressStreet,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formValues.value.addressStreet) = $event)),
              name: "organization-address-street",
              required: ""
            }, null, 8, ["disabled", "modelValue"])
          ]),
          _: 1
        }),
        _createVNode(_unref(BFormGroup), {
          label: "City",
          "label-class": "pb-0 mb-0 mt-1"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BFormInput), {
              disabled: !props.editable,
              modelValue: formValues.value.addressCity,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formValues.value.addressCity) = $event)),
              name: "organization-address-city",
              required: ""
            }, null, 8, ["disabled", "modelValue"])
          ]),
          _: 1
        }),
        _createVNode(_unref(BFormGroup), {
          label: "State",
          "label-class": "pb-0 mb-0 mt-1"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BFormInput), {
              disabled: !props.editable,
              modelValue: formValues.value.addressState,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formValues.value.addressState) = $event)),
              name: "organization-address-state"
            }, null, 8, ["disabled", "modelValue"])
          ]),
          _: 1
        }),
        _createVNode(_unref(BFormGroup), {
          label: "Postal Code",
          "label-class": "pb-0 mb-0 mt-1"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BFormInput), {
              disabled: !props.editable,
              modelValue: formValues.value.addressPostalCode,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((formValues.value.addressPostalCode) = $event)),
              name: "organization-address-postal-code",
              required: ""
            }, null, 8, ["disabled", "modelValue"])
          ]),
          _: 1
        }),
        _createVNode(_unref(BFormGroup), {
          label: "Country",
          state: countryValidationState.value,
          "label-class": "pb-0 mb-0 mt-1"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BFormSelect), {
              disabled: !props.editable,
              options: _unref(CountrySelectOptions),
              state: countryValidationState.value,
              modelValue: formValues.value.addressCountryCode,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((formValues.value.addressCountryCode) = $event)),
              name: "organization-address-country",
              required: ""
            }, null, 8, ["disabled", "options", "state", "modelValue"])
          ]),
          _: 1
        }, 8, ["state"]),
        (props.editable)
          ? (_openBlock(), _createBlock(_unref(BButton), {
              key: 2,
              variant: "primary",
              type: "submit",
              class: "mt-3"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_font_awesome_icon, {
                  icon: ['fas', 'plus'],
                  class: "me-1"
                }),
                (mutationInProgress.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_4, [
                      _cache[10] || (_cache[10] = _createTextVNode(" Saving Changes ", -1)),
                      _createVNode(_unref(BSpinner), {
                        small: "",
                        label: ""
                      })
                    ]))
                  : (mutationShowSuccess.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_5, [
                        _cache[11] || (_cache[11] = _createTextVNode(" Saved ", -1)),
                        _createVNode(_component_font_awesome_icon, {
                          icon: ['fa', 'check-double'],
                          class: "me-2"
                        })
                      ]))
                    : (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(props.buttonText || 'Save'), 1))
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_unref(BAlert), {
      modelValue: mutationError.value,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((mutationError).value = $event)),
      variant: "danger",
      dismissible: "",
      class: "mt-3"
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(mutationError.value), 1)
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})